/**
 * UI Constants for the Advanced Retirement Calculator
 */

import React from "react";
import { styled } from "@mui/material/styles";
import MuiInput from "@mui/material/Input";
import { createCurrencyFormatter, DEFAULT_CURRENCY } from "~/utils/currency";
import type { Currency } from "~/utils/currency";

// MUI Icons
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";

// MUI Icons for deposit category
import SavingsIcon from "@mui/icons-material/Savings";

// MUI Icons for life event icon picker
import HomeIcon from "@mui/icons-material/Home";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SchoolIcon from "@mui/icons-material/School";
import ChildFriendlyIcon from "@mui/icons-material/ChildFriendly";
import FlightIcon from "@mui/icons-material/Flight";
import WorkIcon from "@mui/icons-material/Work";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CelebrationIcon from "@mui/icons-material/Celebration";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import PetsIcon from "@mui/icons-material/Pets";
import BuildIcon from "@mui/icons-material/Build";
import LaptopIcon from "@mui/icons-material/Laptop";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";

// Custom Moneytor Icons
import BankIcon from "~/components/icons/BankIcon";
import ShareIcon from "~/components/icons/ShareIcon";
import CryptoIcon from "~/components/icons/CryptoIcon";
import RealestateIcon from "~/components/icons/RealestateIcon";
import PensionIcon from "~/components/icons/PensionIcon";
import RSUIcon from "~/components/icons/RSUIcon";
import OtherIcon from "~/components/icons/OtherIcon";
import DebtIcon from "~/components/icons/DebtIcon";

import { AssetCategory } from "~/domain/retirement/constants";
import { BRAND_SECONDARY_RGB } from "~/brandColors";
import type { TranslationKey } from "./translations";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

/**
 * Styled input for sliders and small numeric inputs
 */
export const StyledInput = styled(MuiInput)`
  width: 70px;
  & input {
    padding: 4px 8px;
    text-align: center;
  }
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
`;

// ============================================================================
// CATEGORY INFO
// ============================================================================

/**
 * Category display information with labels, icons, and colors
 */
export const getCategoryInfo = (
  t: TranslationKey
): Record<
  AssetCategory,
  { label: string; icon: React.ReactNode; color: string }
> => ({
  [AssetCategory.CASH]: {
    label: t.assumptions.cash,
    icon: <BankIcon width="16" height="16" />,
    color: "#4CAF50",
  },
  [AssetCategory.DEPOSIT]: {
    label: t.assumptions.deposit,
    icon: <SavingsIcon sx={{ fontSize: 16 }} />,
    color: "#66BB6A",
  },
  [AssetCategory.STOCKS]: {
    label: t.assumptions.stocks,
    icon: <ShareIcon width="16" height="16" />,
    color: "#2196F3",
  },
  [AssetCategory.CRYPTO]: {
    label: t.assumptions.crypto,
    icon: <CryptoIcon width="16" height="16" />,
    color: "#FF9800",
  },
  [AssetCategory.RSU]: {
    label: t.assumptions.rsu,
    icon: <RSUIcon width="16" height="16" />,
    color: "#9C27B0",
  },
  [AssetCategory.PENSION]: {
    label: t.assumptions.pension,
    icon: <PensionIcon width="16" height="16" />,
    color: "#00BCD4",
  },
  [AssetCategory.REALESTATE]: {
    label: t.assumptions.realestate,
    icon: <RealestateIcon width="16" height="16" />,
    color: "#795548",
  },
  [AssetCategory.OTHER]: {
    label: t.assumptions.other,
    icon: <OtherIcon width="16" height="16" />,
    color: "#607D8B",
  },
  [AssetCategory.DEBT]: {
    label: t.assumptions.debt,
    icon: <DebtIcon width="16" height="16" />,
    color: "#f44336",
  },
});

// ============================================================================
// EVENT TYPE INFO
// ============================================================================

/**
 * Event type keys for display
 */
export type EventTypeKey = "inflow" | "outflow" | "buy" | "sell" | "loan";

/**
 * Event type display information
 */
export const getEventTypeInfo = (
  t: TranslationKey
): Record<
  EventTypeKey,
  {
    label: string;
    icon: React.ReactNode;
    bgColor: string;
    borderColor: string;
    textColor: string;
  }
> => ({
  inflow: {
    label: t.events.inflow,
    icon: <AttachMoneyIcon fontSize="small" />,
    bgColor: "rgba(var(--color-secondary), 0.08)",
    borderColor: BRAND_SECONDARY_RGB,
    textColor: BRAND_SECONDARY_RGB,
  },
  outflow: {
    label: t.events.outflow,
    icon: <MoneyOffIcon fontSize="small" />,
    bgColor: "rgba(244, 67, 54, 0.08)",
    borderColor: "#f44336",
    textColor: "#d32f2f",
  },
  buy: {
    label: t.ui.eventTypes.buy,
    icon: <ShoppingCartIcon fontSize="small" />,
    bgColor: "rgba(var(--color-secondary), 0.08)",
    borderColor: BRAND_SECONDARY_RGB,
    textColor: BRAND_SECONDARY_RGB,
  },
  sell: {
    label: t.ui.eventTypes.sell,
    icon: <SellIcon fontSize="small" />,
    bgColor: "rgba(244, 67, 54, 0.08)",
    borderColor: "#f44336",
    textColor: "#d32f2f",
  },
  loan: {
    label: t.ui.eventTypes.loan,
    icon: <AccountBalanceIcon fontSize="small" />,
    bgColor: "rgba(255, 152, 0, 0.08)",
    borderColor: "#ff9800",
    textColor: "#e65100",
  },
});

// ============================================================================
// LIFE EVENT ICONS
// ============================================================================

/**
 * Predefined icon options for life events.
 * Keys are persisted in event data; values provide the React element and localized label.
 */
export const getLifeEventIcons = (
  t: TranslationKey
): Record<string, { icon: React.ReactNode; label: string }> => ({
  home: {
    icon: <HomeIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.home,
  },
  car: {
    icon: <DirectionsCarIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.car,
  },
  school: {
    icon: <SchoolIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.school,
  },
  baby: {
    icon: <ChildFriendlyIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.baby,
  },
  family: {
    icon: <FamilyRestroomIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.family,
  },
  flight: {
    icon: <FlightIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.flight,
  },
  work: {
    icon: <WorkIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.work,
  },
  medical: {
    icon: <LocalHospitalIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.medical,
  },
  celebration: {
    icon: <CelebrationIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.celebration,
  },
  gift: {
    icon: <CardGiftcardIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.gift,
  },
  restaurant: {
    icon: <RestaurantIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.restaurant,
  },
  fitness: {
    icon: <FitnessCenterIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.fitness,
  },
  pets: {
    icon: <PetsIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.pets,
  },
  renovation: {
    icon: <BuildIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.renovation,
  },
  laptop: {
    icon: <LaptopIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.laptop,
  },
  trending_up: {
    icon: <TrendingUpIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.trendingUp,
  },
  trending_down: {
    icon: <TrendingDownIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.trendingDown,
  },
  savings: {
    icon: <SavingsIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.savings,
  },
  beach: {
    icon: <BeachAccessIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.beach,
  },
  donate: {
    icon: <VolunteerActivismIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.donate,
  },
  bank: {
    icon: <AccountBalanceIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.bank,
  },
  shopping: {
    icon: <ShoppingCartIcon fontSize="small" />,
    label: t.ui.lifeEventIcons.shopping,
  },
});

// ============================================================================
// FORMATTERS
// ============================================================================

/**
 * Currency formatter – defaults to ILS for backward compatibility.
 * Prefer using `useCurrency().formatter` in React components for
 * dynamic base-currency support.
 */
export const currencyFormatter = createCurrencyFormatter(DEFAULT_CURRENCY);

/**
 * Create a currency formatter for the specified base currency.
 */
export const getCurrencyFormatter = (currency: Currency = DEFAULT_CURRENCY) =>
  createCurrencyFormatter(currency);

/**
 * Percent formatter
 */
export const percentFormatter = new Intl.NumberFormat("he-IL", {
  style: "percent",
  maximumFractionDigits: 1,
});

// ============================================================================
// LEGACY CONSTANTS
// ============================================================================

/**
 * Legacy localStorage key for migration
 */
export const LEGACY_STORAGE_KEY = "advancedRetirementSettings";
